<app-page-breadcrumb pageTitle="Historial de Ejecuciones" />

<div class="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 shadow-sm">
  <div class="p-6">
    <!-- Header y Filtros -->
    <div class="mb-4 flex flex-wrap items-center gap-3">
      <h2 class="mr-auto text-lg font-semibold text-gray-800 dark:text-white">Historial de Ejecuciones</h2>
      <!-- Filtro por job -->
      @if (jobs.length > 1) {
        <select
          [(ngModel)]="selectedJobId"
          (change)="onJobChange()"
          class="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-brand-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300">
          @for (j of jobs; track j.id) {
            <option [ngValue]="j.id">{{ j.description ?? j.jobKey }}</option>
          }
        </select>
      }
      <!-- Filtro por estado -->
      <select
        [(ngModel)]="filterStatus"
        (change)="onFilterChange()"
        class="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-brand-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300">
        <option value="">Todos los estados</option>
        <option value="SUCCESS">Exitoso</option>
        <option value="FAILED">Fallido</option>
        <option value="RUNNING">En ejecución</option>
      </select>
    </div>

    <!-- Loading -->
    @if (loading) {
      <div class="flex items-center justify-center py-12">
        <div class="h-8 w-8 animate-spin rounded-full border-b-2 border-brand-500"></div>
        <span class="ml-3 text-gray-500 dark:text-gray-400">Cargando...</span>
      </div>
    }

    <!-- Error -->
    @if (error) {
      <div class="mb-4 rounded-lg border border-error-200 bg-error-50 p-4 dark:border-error-500/20 dark:bg-error-500/10">
        <p class="text-sm text-error-600 dark:text-error-400">{{ error }}</p>
      </div>
    }

    <!-- No job selected -->
    @if (!loading && !error && selectedJobId == null) {
      <div class="flex flex-col items-center justify-center py-16 text-center">
        <p class="text-sm text-gray-500 dark:text-gray-400">No hay jobs configurados. Crea uno primero en la pantalla de configuración.</p>
      </div>
    }

    <!-- Table -->
    @if (!loading && !error && selectedJobId != null) {
      @if (executions.length === 0) {
        <div class="flex flex-col items-center justify-center py-16 text-center">
          <svg class="mb-4 h-12 w-12 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
          </svg>
          <p class="text-sm text-gray-500 dark:text-gray-400">No hay ejecuciones registradas.</p>
        </div>
      } @else {
        <div class="overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead>
              <tr class="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:border-gray-700 dark:text-gray-400">
                <th class="py-3 px-4">#</th>
                <th class="py-3 px-4">Estado</th>
                <th class="py-3 px-4">Inicio</th>
                <th class="py-3 px-4">Fin</th>
                <th class="py-3 px-4">Duración</th>
                <th class="py-3 px-4">Reintentos</th>
                <th class="py-3 px-4">Tipo</th>
                <th class="py-3 px-4">Mensaje</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100 dark:divide-gray-800">
              @for (e of executions; track e.id) {
                <tr class="transition hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td class="py-3 px-4 text-gray-400 dark:text-gray-500 text-xs">{{ e.id }}</td>
                  <td class="py-3 px-4">
                    <span
                      class="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium"
                      [class]="getStatusClass(e.status)">
                      @if (e.status === 'RUNNING') {
                        <span class="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-yellow-500"></span>
                      }
                      {{ getStatusLabel(e.status) }}
                    </span>
                  </td>
                  <td class="py-3 px-4 text-gray-500 dark:text-gray-400 text-xs">
                    {{ e.startTime ? (e.startTime | date:'dd/MM/yy HH:mm:ss') : '—' }}
                  </td>
                  <td class="py-3 px-4 text-gray-500 dark:text-gray-400 text-xs">
                    {{ e.endTime ? (e.endTime | date:'dd/MM/yy HH:mm:ss') : '—' }}
                  </td>
                  <td class="py-3 px-4 text-gray-600 dark:text-gray-300 text-xs">{{ formatDuration(e.durationMs) }}</td>
                  <td class="py-3 px-4 text-gray-500 dark:text-gray-400 text-xs">
                    @if (e.retryCount > 0) {
                      <span class="text-orange-500">{{ e.retryCount }}</span>
                    } @else {
                      —
                    }
                  </td>
                  <td class="py-3 px-4 text-xs">
                    @if (e.triggeredManually) {
                      <span class="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-500/10 dark:text-blue-400">
                        Manual
                      </span>
                    } @else {
                      <span class="text-gray-400 dark:text-gray-500">Programado</span>
                    }
                  </td>
                  <td class="py-3 px-4">
                    @if (e.message) {
                      <span
                        class="cursor-help text-xs"
                        [class.text-red-500]="e.status === 'FAILED'"
                        [class.dark:text-red-400]="e.status === 'FAILED'"
                        [class.text-gray-500]="e.status !== 'FAILED'"
                        [class.dark:text-gray-400]="e.status !== 'FAILED'"
                        [title]="e.message">
                        {{ truncate(e.message) }}
                      </span>
                    } @else {
                      <span class="text-gray-400 dark:text-gray-500">—</span>
                    }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        @if (totalPages > 1) {
          <div class="mt-4 flex items-center justify-between border-t border-gray-200 pt-4 dark:border-gray-700">
            <p class="text-sm text-gray-500 dark:text-gray-400">Página {{ currentPage + 1 }} de {{ totalPages }}</p>
            <div class="flex gap-2">
              <button
                (click)="loadExecutions(currentPage - 1)"
                [disabled]="currentPage === 0"
                class="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800">
                Anterior
              </button>
              <button
                (click)="loadExecutions(currentPage + 1)"
                [disabled]="currentPage >= totalPages - 1"
                class="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800">
                Siguiente
              </button>
            </div>
          </div>
        }
      }
    }
  </div>
</div>
